import React from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getImagePath } from '../../utils/format/valueFormat';
import { PlayCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TCourse } from '../../models/courses/course';
import ProgressCircle from '../../components/chart/ProgressCircle';
import './CourseSummaryCard.css';

interface CourseSummaryCardProps {
    course: TCourse;
    nextLessonId: number;
    buttonText: string;
    progressPercent: number;
    totalLessons: number;
    completedLessons: number;
    chapters: number;
    epics: number;
}

const CourseSummaryCard: React.FC<CourseSummaryCardProps> = ({
    course,
    nextLessonId,
    buttonText,
    progressPercent,
    totalLessons,
    completedLessons,
    chapters,
    epics,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const goToProgress = (lessonId: number) => {
        navigate(`/course-progression/${lessonId}`);
    };

    return (
        <section className="course-summary">
            <div className="course-summary__media">
                <img
                    alt={course.title}
                    src={getImagePath(course.image)}
                    loading="lazy"
                />
                <div className="course-summary__progress">
                    <ProgressCircle
                        percent={progressPercent}
                        size={84}
                        strokeColor={{
                            '0%': '#2992BF',
                            '100%': '#8BBE1E',
                        }}
                    />
                    <span className="course-summary__progress-label">
                        {t('courses.details.completedLessons', {
                            count: completedLessons,
                            total: totalLessons,
                        })}
                    </span>
                </div>
            </div>

            <div className="course-summary__content">
                <span className="course-summary__eyebrow">
                    {t('courses.details.eyebrow', 'Course mission')}
                </span>
                <h1 className="course-summary__title">{course.title}</h1>
                <p className="course-summary__description">{course.description}</p>

                <div className="course-summary__stats">
                    <div className="course-summary__stat">
                        <strong>{chapters}</strong>
                        <span>{t('courses.details.chapters', 'Chapters')}</span>
                    </div>
                    <div className="course-summary__stat">
                        <strong>{epics}</strong>
                        <span>{t('courses.details.epics', 'Quests')}</span>
                    </div>
                    <div className="course-summary__stat">
                        <strong>{totalLessons}</strong>
                        <span>{t('courses.details.lessons', 'Lessons')}</span>
                    </div>
                </div>

                {course.min_age && course.max_age && (
                    <div className="course-summary__tag">
                        {t('courses.ageRange', {
                            min: course.min_age,
                            max: course.max_age,
                        })}
                    </div>
                )}
            </div>

        <div className="course-summary__cta">
            {nextLessonId > 0 && buttonText ? (
                <>
                    <Button
                        type="primary"
                        size="large"
                        icon={<PlayCircleOutlined />}
                        onClick={() => goToProgress(nextLessonId)}
                    >
                        {t('courses.details.resumeButton', 'Resume mission')}
                    </Button>
                    <p className="course-summary__cta-hint">{buttonText}</p>
                </>
            ) : (
                <p className="course-summary__cta-hint">
                    {t('courses.details.completed', 'All lessons completed!')}
                </p>
            )}
        </div>
        </section>
    );
};

export default CourseSummaryCard;
